/**
 * Analytics Data Transformers
 *
 * Pure functions for transforming analytics data.
 * Extracted from useAnalyticsData.js for testability.
 */

const { DateTime } = require('luxon');

// Graph colors
export const GRAPH_COLORS = {
  GRAY: 'rgb(212,212,212)',
  ORANGE: 'rgb(235,78,0)',
  BLUE: 'rgb(0,110,235)',
};

/**
 * Sum all values in an array
 * @param {Array<number>} arr - Array of numbers
 * @returns {number} Sum of all values
 */
export function sumArray(arr) {
  if (!Array.isArray(arr)) return 0;
  return arr.reduce((acc, val) => acc + val, 0);
}

/**
 * Check if object is iterable
 * @param {*} obj - Object to check
 * @returns {boolean} True if iterable
 */
export function isIterable(obj) {
  return obj == null ? false : typeof obj[Symbol.iterator] === 'function';
}

/**
 * Get total range of data (max - min)
 * @param {Array<number>} data - Array of numbers
 * @returns {number} Range of data
 */
export function getTotalRange(data) {
  if (!isIterable(data) || data.length === 0) return 1;
  const max = Math.max(...data);
  const min = Math.min(...data);
  return max - min || 1; // Return 1 if all values are the same
}

/**
 * Get number of segments for multiple datasets
 * @param {Array<{data: Array<number>}>} datasets - Array of dataset objects
 * @returns {number} Number of segments (1-5)
 */
export function getNumberSegmentsMany(datasets) {
  if (!Array.isArray(datasets) || datasets.length === 0) return 5;

  const ranges = datasets.map((dataset) => {
    if (!dataset || !Array.isArray(dataset.data)) return 0;
    return getTotalRange(dataset.data);
  });

  const maxRange = Math.max(...ranges);
  return Math.min(5, Math.max(1, maxRange));
}

/**
 * Create empty graph state with placeholder data
 * @returns {Object} Empty graph state
 */
export function createEmptyGraphState() {
  return {
    labels: ['NAN'],
    datasets: [{ data: [0], color: () => GRAPH_COLORS.GRAY }],
  };
}

/**
 * Calculate HRO counts from reports
 * @param {Array<Object>} reports - Array of report objects
 * @returns {Object} HRO counts by category
 */
export function calculateHroCounts(reports) {
  const counts = { PF: 0, RS: 0, SO: 0, CR: 0, DE: 0 };

  if (!Array.isArray(reports)) return counts;

  reports.forEach((report) => {
    const hro = report?.HROs?.[0] || report?.HRO_AI;
    if (hro && counts.hasOwnProperty(hro)) {
      counts[hro]++;
    }
  });

  return counts;
}

/**
 * Calculate KPI counts from reports
 * @param {Array<Object>} reports - Array of report objects
 * @returns {Object} KPI counts
 */
export function calculateKpiCounts(reports) {
  const counts = {};

  if (!Array.isArray(reports)) return counts;

  reports.forEach((report) => {
    // User-selected KPIs take priority
    if (report?.KPIs && report.KPIs.length > 0) {
      report.KPIs.forEach((kpi) => {
        counts[kpi] = (counts[kpi] || 0) + 1;
      });
    }
    // AI-predicted KPI only if not overridden
    else if (report?.KPI_AI && !report?.AIOverride) {
      counts[report.KPI_AI] = (counts[report.KPI_AI] || 0) + 1;
    }
  });

  return counts;
}

/**
 * Calculate average response time from reports
 * @param {Array<Object>} reports - Array of report objects
 * @returns {number|string} Average response time in hours or '---'
 */
export function calculateResponseTime(reports) {
  if (!Array.isArray(reports) || reports.length === 0) return '---';

  let totalTime = 0;
  let count = 0;

  reports.forEach((report) => {
    if (report?.timeElapsedUntilResponse != null) {
      totalTime += report.timeElapsedUntilResponse;
      count++;
    }
  });

  if (count === 0) return '---';

  // Convert milliseconds to hours
  const avgHours = totalTime / (1000 * 60 * 60) / count;
  return Number(avgHours.toFixed(2));
}

/**
 * Count open and closed reports
 * @param {Array<Object>} reports - Array of report objects
 * @returns {{open: number, closed: number}} Counts
 */
export function countReportStatus(reports) {
  if (!Array.isArray(reports)) return { open: 0, closed: 0 };

  let open = 0;
  let closed = 0;

  reports.forEach((report) => {
    if (report?.isClosed || report?.closed) {
      closed++;
    } else {
      open++;
    }
  });

  return { open, closed };
}

/**
 * Count urgent reports
 * @param {Array<Object>} reports - Array of report objects
 * @returns {number} Count of urgent reports
 */
export function countUrgentReports(reports) {
  if (!Array.isArray(reports)) return 0;

  return reports.filter((report) => report?.isUrgent || report?.urgent).length;
}

/**
 * Initialize empty time series for given keys
 * @param {number} bucketCount - Number of time buckets
 * @param {Array<string>} keys - Keys to initialize
 * @returns {Object} Time series object
 */
export function initializeTimeSeries(bucketCount, keys) {
  const timeSeries = {};

  if (!Array.isArray(keys)) return timeSeries;

  keys.forEach((key) => {
    timeSeries[key] = new Array(bucketCount).fill(0);
  });

  // Always include ALL
  timeSeries['ALL'] = new Array(bucketCount).fill(0);

  return timeSeries;
}

/**
 * Populate time series data for a single report
 * @param {Array<DateTime>} bucketStartTimes - Array of bucket start times
 * @param {DateTime} reportDate - Report timestamp as DateTime
 * @param {Object} report - Report object
 * @param {Object} timeSeries - Time series object to populate
 * @param {number} timeAmount - Total number of buckets
 */
export function populateTimeSeries(bucketStartTimes, reportDate, report, timeSeries, timeAmount) {
  if (report == null || !reportDate || !bucketStartTimes) return;

  let found = false;
  const reversedBuckets = [...bucketStartTimes].reverse();

  reversedBuckets.forEach((bucketStart, index) => {
    if (found) return;

    const isBigger = reportDate.toMillis() >= bucketStart.toMillis();

    if (isBigger) {
      const bucketIndex = timeAmount - index - 1;

      // Handle HROs
      if (report.HROs && report.HROs.length > 0) {
        report.HROs.forEach((HRO) => {
          if (!(HRO in timeSeries)) {
            timeSeries[HRO] = new Array(timeAmount).fill(0);
          }
          timeSeries[HRO][bucketIndex] += 1;
        });
      } else if (report.HRO_AI) {
        if (!(report.HRO_AI in timeSeries)) {
          timeSeries[report.HRO_AI] = new Array(timeAmount).fill(0);
        }
        timeSeries[report.HRO_AI][bucketIndex] += 1;
      }

      // Handle KPIs
      if (report.KPIs && report.KPIs.length > 0) {
        report.KPIs.forEach((KPI) => {
          if (!(KPI in timeSeries)) {
            timeSeries[KPI] = new Array(timeAmount).fill(0);
          }
          timeSeries[KPI][bucketIndex] += 1;
        });
      }

      // For KPI_AI, only include if not overridden
      if (report.KPI_AI && !report.AIOverride) {
        if (!(report.KPI_AI in timeSeries)) {
          timeSeries[report.KPI_AI] = new Array(timeAmount).fill(0);
        }
        timeSeries[report.KPI_AI][bucketIndex] += 1;
      }

      // All reports
      if (!('ALL' in timeSeries)) {
        timeSeries['ALL'] = new Array(timeAmount).fill(0);
      }
      timeSeries['ALL'][bucketIndex] += 1;

      found = true;
    }
  });
}

/**
 * Get sorted KPI data for display
 * @param {Object} kpiCounts - KPI counts object
 * @param {number} limit - Maximum number of KPIs to return
 * @returns {Array<{label: string, value: number}>} Sorted KPI data
 */
export function getSortedKpiData(kpiCounts, limit = 5) {
  if (!kpiCounts || typeof kpiCounts !== 'object') return [];

  return Object.entries(kpiCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([label, value]) => ({ label, value }));
}

/**
 * Convert HRO counts to distribution format for pie chart
 * @param {Object} hroCounts - HRO counts object
 * @param {Object} hroLabels - HRO label mappings
 * @param {Object} hroColors - HRO color mappings
 * @returns {Array<{label: string, value: number, color: string}>} Distribution data
 */
export function formatHroDistribution(hroCounts, hroLabels = {}, hroColors = {}) {
  if (!hroCounts || typeof hroCounts !== 'object') return [];

  return Object.entries(hroCounts)
    .filter(([_, value]) => value > 0)
    .map(([key, value]) => ({
      label: hroLabels[key] || key,
      value,
      color: hroColors[key] || '#999',
    }));
}
